/**
 * NegotiationPanel Component
 * Main negotiation automation interface with email sequences, posturing detection,
 * and multi-deal volume management.
 *
 * Ed Dua requirement (March 9, 2026): "How do you do that in a subtle automated way
 * so you can do a volume of offers?"
 */

import { useState, useMemo } from 'react';
import {
  Mail,
  Clock,
  TrendingUp,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  MessageSquare,
  Calendar,
  Activity,
  BarChart3,
  Send,
} from 'lucide-react';
import { cn, formatDate, formatRelativeTime } from '@/lib/utils';
import type { Deal, DealWithFund } from '@/types/database';
import type {
  NegotiationThread,
  EmailSequence,
  PosturingSignal,
  NegotiationActivity,
  EmailTemplate,
} from '@/types/negotiation';
import {
  negotiationStageLabels,
  negotiationStageColors,
  posturingIndicatorLabels,
  posturingIndicatorColors,
  posturingIndicatorIcons,
  emailSequenceStatusLabels,
  emailSequenceStatusColors,
  getDaysSinceLastContact,
  getResponseRate,
  detectPosturingFromActivity,
  NegotiationStage,
  PosturingIndicator,
  EmailSequenceStatus,
} from '@/types/negotiation';
import { EmailTemplateSelector } from './EmailTemplateSelector';

// ============================================================================
// MOCK DATA (Replace with API calls in production)
// ============================================================================

const getMockNegotiationThread = (dealId: string): NegotiationThread => ({
  id: `thread-${dealId}`,
  deal_id: dealId,
  stage: NegotiationStage.PRICING_DISCUSSION,
  posturing_indicator: PosturingIndicator.COOPERATIVE,
  last_contact_date: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(), // 3 days ago
  next_suggested_action: 'Send counter-offer with revised pricing',
  next_action_date: new Date(Date.now() + 1 * 24 * 60 * 60 * 1000).toISOString(), // Tomorrow
  response_time_avg_hours: 36,
  total_emails_sent: 5,
  total_emails_received: 4,
  sentiment_score: 0.7,
  created_at: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString(),
  updated_at: new Date().toISOString(),
});

const getMockEmailSequences = (threadId: string): EmailSequence[] => [
  {
    id: `seq-1-${threadId}`,
    negotiation_thread_id: threadId,
    template_id: '1',
    status: EmailSequenceStatus.REPLIED,
    scheduled_send_date: null,
    sent_date: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString(),
    opened_date: new Date(Date.now() - 13 * 24 * 60 * 60 * 1000).toISOString(),
    replied_date: new Date(Date.now() - 12 * 24 * 60 * 60 * 1000).toISOString(),
    subject: 'Interest in Sequoia Fund VIII LP Position',
    body: 'Initial outreach email...',
    recipient_email: 'seller@example.com',
    recipient_name: 'John Seller',
    metadata: {},
    created_at: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: `seq-2-${threadId}`,
    negotiation_thread_id: threadId,
    template_id: '3',
    status: EmailSequenceStatus.OPENED,
    scheduled_send_date: null,
    sent_date: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
    opened_date: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    replied_date: null,
    subject: 'Revised Proposal for Sequoia Fund VIII',
    body: 'Counter-offer email...',
    recipient_email: 'seller@example.com',
    recipient_name: 'John Seller',
    metadata: {},
    created_at: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
  },
];

const getMockActivities = (threadId: string): NegotiationActivity[] => [
  {
    id: `activity-1-${threadId}`,
    negotiation_thread_id: threadId,
    activity_type: 'email_sent',
    description: 'Sent initial outreach email',
    actor: 'Ed Dua',
    metadata: { template_id: '1' },
    created_at: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: `activity-2-${threadId}`,
    negotiation_thread_id: threadId,
    activity_type: 'email_received',
    description: 'Received positive response - interested in discussing',
    actor: null,
    metadata: { sentiment: 'positive' },
    created_at: new Date(Date.now() - 12 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: `activity-3-${threadId}`,
    negotiation_thread_id: threadId,
    activity_type: 'call',
    description: 'Intro call - discussed pricing and timeline',
    actor: 'Ed Dua',
    metadata: { duration_minutes: 30 },
    created_at: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: `activity-4-${threadId}`,
    negotiation_thread_id: threadId,
    activity_type: 'email_sent',
    description: 'Sent counter-offer with revised pricing',
    actor: 'Ed Dua',
    metadata: { template_id: '3' },
    created_at: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: `activity-5-${threadId}`,
    negotiation_thread_id: threadId,
    activity_type: 'note',
    description: 'Seller opened email but has not responded yet',
    actor: 'System',
    metadata: {},
    created_at: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
  },
];

// ============================================================================
// COMPONENT PROPS
// ============================================================================

interface NegotiationPanelProps {
  deal: Deal | DealWithFund;
  className?: string;
}

// ============================================================================
// COMPONENT
// ============================================================================

export function NegotiationPanel({ deal, className }: NegotiationPanelProps): React.ReactElement {
  const [isExpanded, setIsExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'sequences' | 'activity' | 'templates'>(
    'overview'
  );

  // Mock data (replace with API hooks in production)
  const negotiationThread = getMockNegotiationThread(deal.id);
  const emailSequences = getMockEmailSequences(negotiationThread.id);
  const activities = getMockActivities(negotiationThread.id);

  // Calculate posturing signal
  const posturingSignal: PosturingSignal = useMemo(() => {
    const daysSince = getDaysSinceLastContact(negotiationThread.last_contact_date);
    const responseRate = getResponseRate(
      negotiationThread.total_emails_sent,
      negotiationThread.total_emails_received
    );

    return detectPosturingFromActivity(
      daysSince,
      responseRate,
      negotiationThread.response_time_avg_hours
    );
  }, [negotiationThread]);

  const responseRate = getResponseRate(
    negotiationThread.total_emails_sent,
    negotiationThread.total_emails_received
  );

  const daysSinceContact = getDaysSinceLastContact(negotiationThread.last_contact_date);

  const handleTemplateSelect = (
    template: EmailTemplate,
    filledContent: { subject: string; body: string }
  ): void => {
    // In production, this would open email client or create draft
    console.log('Template selected:', { template, filledContent });
    alert(`Template "${template.name}" ready to send!\n\nSubject: ${filledContent.subject}`);
  };

  return (
    <div className={cn('bg-white rounded-lg border border-gray-200', className)}>
      {/* Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full p-6 flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <MessageSquare className="h-5 w-5 text-blue-600" />
          <h3 className="text-lg font-semibold text-gray-900">Negotiation Automation</h3>
          <span
            className={cn(
              'px-2 py-1 text-xs font-medium rounded-full border',
              negotiationStageColors[negotiationThread.stage]
            )}
          >
            {negotiationStageLabels[negotiationThread.stage]}
          </span>
        </div>
        {isExpanded ? (
          <ChevronUp className="h-5 w-5 text-gray-400" />
        ) : (
          <ChevronDown className="h-5 w-5 text-gray-400" />
        )}
      </button>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="border-t border-gray-200">
          {/* Tabs */}
          <div className="flex border-b border-gray-200">
            {[
              { key: 'overview' as const, label: 'Overview', icon: BarChart3 },
              { key: 'sequences' as const, label: 'Email Sequences', icon: Mail },
              { key: 'activity' as const, label: 'Activity', icon: Activity },
              { key: 'templates' as const, label: 'Templates', icon: Send },
            ].map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                onClick={() => setActiveTab(key)}
                className={cn(
                  'flex-1 px-4 py-3 text-sm font-medium transition-colors flex items-center justify-center gap-2',
                  activeTab === key
                    ? 'border-b-2 border-blue-600 text-blue-600'
                    : 'text-gray-600 hover:text-gray-900'
                )}
              >
                <Icon className="h-4 w-4" />
                {label}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="space-y-6">
                {/* Posturing Detection */}
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-200">
                  <div className="flex items-start gap-3">
                    <div className="text-3xl">
                      {posturingIndicatorIcons[posturingSignal.indicator]}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h4 className="font-semibold text-gray-900">Posturing Detection</h4>
                        <span
                          className={cn(
                            'px-2 py-0.5 text-xs font-medium rounded-full border',
                            posturingIndicatorColors[posturingSignal.indicator]
                          )}
                        >
                          {posturingIndicatorLabels[posturingSignal.indicator]}
                        </span>
                        <span className="text-xs text-gray-600">
                          {posturingSignal.confidence}% confident
                        </span>
                      </div>
                      <div className="space-y-2">
                        {posturingSignal.signals.map((signal, idx) => (
                          <div key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                            <span className="text-blue-600 mt-0.5">•</span>
                            {signal}
                          </div>
                        ))}
                      </div>
                      <div className="mt-3 p-3 bg-white rounded border border-blue-200">
                        <p className="text-sm font-medium text-blue-900 mb-1">
                          Recommended Action:
                        </p>
                        <p className="text-sm text-blue-700">
                          {posturingSignal.recommended_action}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Key Metrics */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-1">
                      <Clock className="h-4 w-4 text-gray-500" />
                      <p className="text-sm text-gray-600">Last Contact</p>
                    </div>
                    <p className="text-lg font-bold text-gray-900">
                      {daysSinceContact !== null ? `${daysSinceContact}d ago` : 'Never'}
                    </p>
                  </div>

                  <div className="p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-1">
                      <TrendingUp className="h-4 w-4 text-gray-500" />
                      <p className="text-sm text-gray-600">Response Rate</p>
                    </div>
                    <p className="text-lg font-bold text-gray-900">{responseRate}%</p>
                  </div>

                  <div className="p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-1">
                      <Mail className="h-4 w-4 text-gray-500" />
                      <p className="text-sm text-gray-600">Emails Exchanged</p>
                    </div>
                    <p className="text-lg font-bold text-gray-900">
                      {negotiationThread.total_emails_sent + negotiationThread.total_emails_received}
                    </p>
                  </div>

                  <div className="p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-1">
                      <Clock className="h-4 w-4 text-gray-500" />
                      <p className="text-sm text-gray-600">Avg Response</p>
                    </div>
                    <p className="text-lg font-bold text-gray-900">
                      {negotiationThread.response_time_avg_hours !== null
                        ? `${negotiationThread.response_time_avg_hours}h`
                        : 'N/A'}
                    </p>
                  </div>
                </div>

                {/* Next Action */}
                {negotiationThread.next_suggested_action && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5" />
                      <div className="flex-1">
                        <h4 className="font-semibold text-amber-900 mb-1">Next Action</h4>
                        <p className="text-sm text-amber-800">
                          {negotiationThread.next_suggested_action}
                        </p>
                        {negotiationThread.next_action_date && (
                          <p className="text-xs text-amber-700 mt-2">
                            <Calendar className="h-3 w-3 inline mr-1" />
                            Suggested: {formatDate(negotiationThread.next_action_date)}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Email Sequences Tab */}
            {activeTab === 'sequences' && (
              <div className="space-y-4">
                {emailSequences.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    No email sequences yet. Use templates to start your first sequence.
                  </div>
                ) : (
                  emailSequences.map((sequence) => (
                    <div
                      key={sequence.id}
                      className="p-4 border border-gray-200 rounded-lg hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <h4 className="font-semibold text-gray-900">{sequence.subject}</h4>
                          <p className="text-sm text-gray-600 mt-1">
                            To: {sequence.recipient_name} ({sequence.recipient_email})
                          </p>
                        </div>
                        <span
                          className={cn(
                            'px-2 py-1 text-xs font-medium rounded-full',
                            emailSequenceStatusColors[sequence.status]
                          )}
                        >
                          {emailSequenceStatusLabels[sequence.status]}
                        </span>
                      </div>

                      <div className="flex items-center gap-4 text-xs text-gray-600">
                        {sequence.sent_date && (
                          <span>Sent: {formatRelativeTime(sequence.sent_date)}</span>
                        )}
                        {sequence.opened_date && (
                          <span>Opened: {formatRelativeTime(sequence.opened_date)}</span>
                        )}
                        {sequence.replied_date && (
                          <span className="text-green-600 font-medium">
                            Replied: {formatRelativeTime(sequence.replied_date)}
                          </span>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Activity Tab */}
            {activeTab === 'activity' && (
              <div className="space-y-4">
                {activities.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">No activity yet</div>
                ) : (
                  <div className="relative">
                    {/* Timeline line */}
                    <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200" />

                    {/* Activities */}
                    <div className="space-y-6">
                      {activities.map((activity) => (
                        <div key={activity.id} className="relative flex items-start gap-4">
                          {/* Timeline dot */}
                          <div
                            className={cn(
                              'relative z-10 flex items-center justify-center w-8 h-8 rounded-full border-2 bg-white',
                              activity.activity_type === 'email_sent' &&
                                'border-blue-500 text-blue-500',
                              activity.activity_type === 'email_received' &&
                                'border-green-500 text-green-500',
                              activity.activity_type === 'call' &&
                                'border-purple-500 text-purple-500',
                              activity.activity_type === 'note' &&
                                'border-gray-400 text-gray-400',
                              activity.activity_type === 'meeting' &&
                                'border-amber-500 text-amber-500'
                            )}
                          >
                            {activity.activity_type === 'email_sent' && (
                              <Mail className="h-4 w-4" />
                            )}
                            {activity.activity_type === 'email_received' && (
                              <Mail className="h-4 w-4" />
                            )}
                            {activity.activity_type === 'call' && (
                              <MessageSquare className="h-4 w-4" />
                            )}
                            {activity.activity_type === 'note' && (
                              <AlertCircle className="h-4 w-4" />
                            )}
                          </div>

                          {/* Activity content */}
                          <div className="flex-1 pt-1">
                            <p className="text-sm text-gray-900">{activity.description}</p>
                            <p className="text-xs text-gray-500 mt-1">
                              {activity.actor && `${activity.actor} • `}
                              {formatRelativeTime(activity.created_at)}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Templates Tab */}
            {activeTab === 'templates' && (
              <div>
                <div className="mb-4">
                  <h4 className="font-semibold text-gray-900 mb-2">Email Templates</h4>
                  <p className="text-sm text-gray-600">
                    Select a template to send to {deal.seller_name}. Templates will be automatically
                    filled with deal information.
                  </p>
                </div>
                <EmailTemplateSelector deal={deal} onTemplateSelect={handleTemplateSelect} />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
